// The standard page for a table of records, in the order every page has (Mitch, 2026-11-04: "Hierarchy in design
// matters"): the page's header (its title, what the whole page covers, its actions and New), then its tabs or notices
// (`banner`), then the table as one unit: its bar (View, Columns, search, the filters and buttons in `toolbar`) on top
// of its rows. The open record is in the side pane (?open=<id>, so every record has a shareable URL). Keys: / search,
// n new, Esc close.
import { Plus, Search } from "lucide-react";
import { type ReactNode, type Ref, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { setSearch, useSearch } from "../lib/router.tsx";
import { Button, cx, Input, Kbd } from "./controls.tsx";
import { type Column, DataTable } from "./DataTable.tsx";
import { PageHeader } from "./PageParts.tsx";
import { openPane, paneIsOpen, usePaneParam, usePaneRoot } from "./SidePane.tsx";
import { useTableText } from "./table-text.ts";

/**
 * A page's filter box: the page's "/" focuses it (the hint on its right), Esc clears it and leaves it, and with
 * `toTable` ↓ or Enter moves into the table (`[data-table]`).
 */
export function PageSearch({
  ref,
  value,
  onChange,
  placeholder,
  toTable = false,
  className,
}: {
  ref?: Ref<HTMLInputElement>;
  value: string;
  onChange: (text: string) => void;
  placeholder?: string;
  toTable?: boolean;
  className?: string;
}) {
  const t = useTableText();
  return (
    <div className={cx("relative", className)}>
      <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-faint" />
      <Input
        ref={ref}
        value={value}
        placeholder={placeholder ?? t.filter}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onChange("");
            e.currentTarget.blur();
          }
          if (toTable && (e.key === "ArrowDown" || e.key === "Enter")) {
            e.preventDefault();
            (document.querySelector("[data-table]") as HTMLElement | null)?.focus();
          }
        }}
        className="pr-8 pl-8"
      />
      <Kbd className="absolute top-2 right-2">/</Kbd>
    </div>
  );
}

/** A plan page's keys: / to its filter box; Esc closes its detail (`?item=`), unless the side pane or a popover is open. */
export function useDetailKeys(searchRef: RefObject<HTMLInputElement | null>, detailOpen: boolean) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape" && detailOpen && !paneIsOpen() && !document.querySelector("[data-popover]")) setSearch({ item: null });
      if (isTyping(e.target)) return;
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchRef, detailOpen]);
}

export function isTyping(target: EventTarget | null) {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
}

/** Whether a one-row header has to stack: true once its row is wider than it (a narrow window, a record open beside
 * the table), false again when it's as wide as that row needed or its text changed (another tab's buttons: tried in one
 * row again, which moves nothing in the page: stacking only reorders with CSS `order`). Checked before paint on every
 * render and whenever it or what's in it changes size. In one row only the search (and a truncating total) gives way;
 * nothing else shrinks or wraps, so `needs` is the row's real minimum. ListPage marks its header `data-stacked` meanwhile, for
 * controls that may wrap only then (`[[data-stacked]_&]:flex-wrap`). Tab follows the page: the one-row order. */
export function useStacked(header: RefObject<HTMLElement | null>) {
  const [stacked, setStacked] = useState(false);
  const needs = useRef(0); // the one row's width, read when it last didn't fit
  const said = useRef(""); // the header's text then
  useLayoutEffect(() => {
    const el = header.current;
    if (!el) return;
    const fit = () => {
      const text = textOf(el);
      if (stacked && text !== said.current) needs.current = 0;
      said.current = text;
      // How much wider the row has to be: what runs into the right padding or past it, or what a box that gave way
      // (the totals) can't hold
      const box = el.getBoundingClientRect();
      const end = box.right - Number.parseFloat(getComputedStyle(el).paddingRight);
      const kids = [...el.children];
      const over = Math.max(0, ...kids.map((c) => c.getBoundingClientRect().right - end), ...kids.map((c) => c.scrollWidth - c.clientWidth));
      if (!stacked && over > 1) {
        needs.current = el.clientWidth + over;
        setStacked(true);
      } else if (stacked && el.clientWidth >= needs.current) setStacked(false);
    };
    fit();
    // Its controls too: one that changes on its own (Auto mode's "2m ago · 3") can widen the row without a render here
    const watch = new ResizeObserver(fit);
    for (const node of [el, ...el.querySelectorAll(":scope > *, :scope > * > *")]) watch.observe(node);
    return () => watch.disconnect();
  });
  return stacked;
}

/** A header's words in any order and without its numbers: moving things about, or a count, total or "2m ago" changing
 * with every keystroke or tick, is no new header to try in one row again (another tab's buttons are) */
function textOf(el: HTMLElement) {
  const texts: string[] = [];
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  while (walk.nextNode()) texts.push((walk.currentNode.nodeValue ?? "").replace(/\d+/g, "0"));
  return texts.sort().join("\n");
}

export function ListPage<T extends { id: string }>({
  title,
  tableName,
  rows,
  columns,
  defaultSort,
  newLabel,
  onNew,
  pane,
  panel,
  toolbar,
  empty,
  grouping,
  editing,
  selection,
  banner,
  scope,
  actions,
  onOpen,
  header = true,
}: {
  title: ReactNode;
  /** The table's name for Copy / CSV and its views, when the title isn't plain text (or changes with the language). */
  tableName?: string;
  rows: T[];
  columns: Column<T>[];
  defaultSort?: { key: string; desc?: boolean };
  newLabel?: string;
  /** Opens the create flow; omit to hide the New button. */
  onNew?: () => void;
  /** Side pane --- the kind of record a row opens in the side pane (web/src/panes.tsx); `?open=<id>` links open it there
   * too. Without one, `panel` shows the record beside the list. */
  pane?: string;
  panel?: (id: string, close: () => void) => ReactNode;
  toolbar?: ReactNode;
  empty?: ReactNode;
  /** Group rows under header rows (see DataTable's groupBy). */
  grouping?: { by: (row: T) => string | null; groupNoun: [string, string]; rowNoun: [string, string] };
  editing?: boolean;
  selection?: { selected: Set<string>; onChange: (next: Set<string>) => void };
  /** Shown between the header and the table: the page's tabs, a notice. */
  banner?: ReactNode;
  /** What the whole page covers (a CM, a project), beside the title. */
  scope?: ReactNode;
  /** The page's own buttons, before New (not the table's: those go in `toolbar`). */
  actions?: ReactNode;
  /** What a row's click does, when it isn't opening the record beside the list (a run opens in its project). */
  onOpen?: (row: T) => void;
  /** false: no header of its own, for a tab under another page's header (Settings' Users): `actions` and New go on the
   * right of the table's bar, and `scope` isn't shown. */
  header?: boolean;
}) {
  const params = useSearch();
  const inPane = usePaneRoot(pane);
  const openId = pane ? inPane : params.get("open");
  usePaneParam("open", pane);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const close = () => setSearch({ open: null });

  // ?new=1 (from the command palette) opens the create flow once.
  const wantsNew = params.get("new") === "1";
  useEffect(() => {
    if (!wantsNew) return;
    setSearch({ new: null });
    onNew?.();
  }, [wantsNew, onNew]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape" && !pane && openId && !document.querySelector("[data-dialog]")) {
        close();
        return;
      }
      if (isTyping(e.target)) return;
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "n" && onNew) {
        e.preventDefault();
        onNew();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const buttons = (actions || onNew) && (
    <>
      {actions}
      {onNew && (
        <Button variant="primary" icon={<Plus className="size-4" />} kbd="N" onClick={onNew}>
          {newLabel ?? "New"}
        </Button>
      )}
    </>
  );
  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        {header && (
          <PageHeader title={title} actions={buttons}>
            {scope}
          </PageHeader>
        )}
        {banner}
        <div className="min-h-0 flex-1 bg-surface">
          <DataTable
            rows={rows}
            columns={columns}
            search={query}
            activeId={openId}
            defaultSort={defaultSort}
            onOpen={(r) => (onOpen ? onOpen(r) : pane ? openPane({ kind: pane, id: r.id }, { root: true, replace: !!openId }) : setSearch({ open: r.id }))}
            empty={empty}
            groupBy={grouping?.by}
            groupNoun={grouping?.groupNoun}
            rowNoun={grouping?.rowNoun}
            editing={editing}
            selection={selection}
            exportName={tableName ?? String(title)}
            bar={{
              search: <PageSearch ref={searchRef} value={query} onChange={setQuery} toTable className="max-w-64 min-w-32 flex-1 basis-40" />,
              filters: toolbar,
              actions: header ? undefined : buttons,
            }}
          />
        </div>
      </div>
      {!pane && openId && panel?.(openId, close)}
    </div>
  );
}
